import { fCurrency } from '../../../../../utils/formatNumber';
import { displayPerilRate } from '../../../../../pages/dashboard/PremiumOverrides/utility';
import {
  vagoAdditionalCoveragePerilNames,
  VagoAdditionalCoveragePerilNames,
} from '../../../../../pages/dashboard/PremiumOverrides/Vago/constants';
import type { QuoteInsurancePoliciesQuery } from '../../../../../../../gql/graphql';

type PolicyType = NonNullable<
  NonNullable<QuoteInsurancePoliciesQuery['quoteInsurancePolicies']>[number]
>;
type NonNullPolicyType = NonNullable<PolicyType>;
export type CoverageType = NonNullPolicyType['coverages'][number];
export type PerilType = CoverageType['perils'][number];

export interface GroupedCoverages {
  propertyByLocation: Map<string, Array<CoverageType>>;
  liability: Array<CoverageType>;
  misc: Array<PerilType>;
}

export const isZero = (value: number | string | null | undefined): boolean => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return true;
    const numValue = Number(trimmed);
    return !isNaN(numValue) && numValue === 0;
  }
  return value === 0;
};

export const formatCurrency = (value: number | string): string => {
  if (isZero(value)) return '-';
  const numValue = typeof value === 'string' ? Number(value) : value;
  return fCurrency(numValue);
};

export const formatRate = (value: number | string): string => {
  if (isZero(value)) return '-';
  const numValue = typeof value === 'string' ? Number(value) : value;
  return displayPerilRate(numValue, { displayUnit: true });
};

export const getFirstCoveredLocationDescription = (
  coverage: CoverageType,
): string => {
  const firstLocation = coverage.coveredLocations?.[0];
  return firstLocation
    ? firstLocation.description || firstLocation.address || 'Unknown Location'
    : 'No Location';
};

export const groupCoverages = (
  coverages: Array<CoverageType>,
): GroupedCoverages => {
  return coverages.reduce<GroupedCoverages>(
    (grouped, coverage) => {
      // Separate perils into additional coverage perils and regular perils
      const additionalCoveragePerils: Array<PerilType> = [];
      const regularPerils: Array<PerilType> = [];

      // Get address for Equipment Breakdown perils (only in Property coverages)
      const locationAddress = getFirstCoveredLocationDescription(coverage);

      (coverage.perils || []).forEach((peril) => {
        if (
          vagoAdditionalCoveragePerilNames.some(
            (perilName) => perilName === peril.name,
          )
        ) {
          // Modify Equipment Breakdown perils from Property coverages to "Boiler - {address}"
          if (
            peril.name ===
              VagoAdditionalCoveragePerilNames.EQUIPMENT_BREAKDOWN &&
            coverage.name === 'Property'
          ) {
            additionalCoveragePerils.push({
              ...peril,
              name: `Boiler - ${locationAddress}`,
            });
          } else {
            additionalCoveragePerils.push(peril);
          }
        } else {
          regularPerils.push(peril);
        }
      });

      // Add additional coverage perils to misc
      grouped.misc.push(...additionalCoveragePerils);

      // Create a new coverage with only the regular perils
      const coverageWithRegularPerils = {
        ...coverage,
        perils: regularPerils,
      };

      if (coverage.name === 'Property') {
        // Group by covered location - use first location or default
        const locationKey = getFirstCoveredLocationDescription(coverage);

        if (!grouped.propertyByLocation.has(locationKey)) {
          grouped.propertyByLocation.set(locationKey, []);
        }
        const locationCoverages = grouped.propertyByLocation.get(locationKey);
        // This should always be defined since we just set it above if it didn't exist
        if (locationCoverages) {
          locationCoverages.push(coverageWithRegularPerils);
        }
      } else if (coverage.name === 'Liability') {
        grouped.liability.push(coverageWithRegularPerils);
      }
      // For other coverages, we don't add them anywhere (only their additional coverage perils go to misc)
      return grouped;
    },
    {
      propertyByLocation: new Map<string, Array<CoverageType>>(),
      liability: [],
      misc: [],
    },
  );
};
